import React, { useCallback, useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import EventRegistrationLink from "../../Components/EventRegistrationLink";
import { DeleteIcon, EditIcon } from "../../Components/Icons/ActionIcons";
import apiService from "../../services/api.service";
import { getRole, ROLES } from "../../utils/roles";
import { formatRomeDateTime, isSameRomeDay, parseApiDate, toRomeDatetimeLocal } from "../../utils/datetime";

const RequiredLabel = ({ children }) => (
  <>
    {children} <span className="form-required" aria-hidden="true">*</span>
  </>
);

const emptyForm = () => ({
  name: "",
  description: "",
  embed_link: "",
  date: "",
  coverFile: null,
  application_id: "",
});

const DashboardGestoreEvents = ({ loginStatus }) => {
  const isSuperadmin = getRole(loginStatus) === ROLES.SUPERADMIN;
  const [events, setEvents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteUid, setDeleteUid] = useState("");
  const [attendanceTarget, setAttendanceTarget] = useState(null);
  const [attendanceUsers, setAttendanceUsers] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState("");
  const [reportBusy, setReportBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getEvents();
      setEvents(response.data || []);
      if (isSuperadmin) {
        const appsRes = await apiService.getApplications();
        setApplications(appsRes.data || []);
      }
    } catch {
      setError("Impossibile caricare gli eventi.");
    } finally {
      setLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    load();
  }, [load]);

  const now = new Date();
  const inProgress = events.filter((e) => !e.finished && isSameRomeDay(e.date, now));
  const upcoming = events.filter((e) => !e.finished && parseApiDate(e.date) > now && !isSameRomeDay(e.date, now));
  const past = events.filter((e) => e.finished || (parseApiDate(e.date) < now && !isSameRomeDay(e.date, now)));

  const closeForm = () => {
    setShowForm(false);
    setFormError("");
  };

  const openCreate = () => {
    setEditingEvent(null);
    setForm(emptyForm());
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      name: event.name || "",
      description: event.description || "",
      embed_link: event.embed_link || "",
      date: toRomeDatetimeLocal(event.date),
      coverFile: null,
    });
    setFormError("");
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    setSuccess("");
    const payloadDate = new Date(form.date).toISOString();
    try {
      if (editingEvent) {
        await apiService.modifyEvent(
          payloadDate,
          form.name,
          form.description,
          form.embed_link,
          editingEvent.finished,
          editingEvent.id
        );
        if (form.coverFile) {
          try {
            await apiService.uploadEventCover(editingEvent.id, form.coverFile);
          } catch (coverErr) {
            if (coverErr.response?.status === 413) {
              setFormError(
                "Evento aggiornato, ma la copertina è troppo grande (errore 413). Riduci l'immagine oppure alza client_max_body_size sul proxy."
              );
              setShowForm(false);
              await load();
              return;
            }
            throw coverErr;
          }
        }
        setSuccess("Evento aggiornato.");
      } else {
        const created = await apiService.createEvent(
          payloadDate,
          form.name,
          form.description,
          form.embed_link,
          null,
          isSuperadmin ? Number(form.application_id) : null
        );
        if (form.coverFile) {
          try {
            await apiService.uploadEventCover(created.data.id, form.coverFile);
          } catch (coverErr) {
            const status = coverErr.response?.status;
            if (status === 413) {
              setFormError(
                "Evento creato, ma la copertina è troppo grande per il server (errore 413). Riduci l'immagine oppure alza client_max_body_size sul proxy Nginx/NPM."
              );
              setShowForm(false);
              await load();
              return;
            }
            throw coverErr;
          }
        }
        setSuccess(`Evento creato. ID: ${created.data.id}`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError("");
    try {
      await apiService.deleteEvent(deleteTarget.id, deleteUid.trim());
      setSuccess("Evento eliminato.");
      setDeleteTarget(null);
      setDeleteUid("");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Eliminazione non riuscita.");
    } finally {
      setSaving(false);
    }
  };

  const openAttendance = async (event) => {
    setAttendanceTarget(event);
    setAttendanceUsers([]);
    setAttendanceError("");
    setAttendanceLoading(true);
    try {
      const response = await apiService.getEventAttendance(event.id);
      setAttendanceUsers(response.data || []);
    } catch (err) {
      setAttendanceError(err.response?.data?.detail || "Impossibile caricare le presenze.");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const downloadAttendance = async (event, format) => {
    const busyKey = `${event.id}-${format}`;
    setReportBusy(busyKey);
    setError("");
    try {
      await apiService.downloadEventAttendance(event.id, format);
    } catch (err) {
      setError(err.response?.data?.detail || `Download ${format.toUpperCase()} non riuscito.`);
    } finally {
      setReportBusy("");
    }
  };

  const EventSection = ({ title, items, showReports = false }) => (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: "1.15rem", marginBottom: 12 }}>{title}</h2>
      {items.length === 0 ? (
        <Alert variant="info">Nessun evento in questa sezione.</Alert>
      ) : (
        <div className="dashboard-events-list">
          {items.map((event) => (
            <div className="dashboard-event-item" key={event.id}>
              <div>
                <p className="dashboard-event-item__title">{event.name}</p>
                <p className="dashboard-event-item__meta">{event.description}</p>
                <p className="dashboard-event-item__meta">{formatRomeDateTime(event.date)}</p>
                <p className="dashboard-event-item__id">
                  ID: <span>{event.id}</span>
                </p>
              </div>
              <div className="dashboard-event-item__actions">
                <EventRegistrationLink event={event} />
                {showReports && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    aria-label={`Visualizza presenze ${event.name}`}
                    onClick={() => openAttendance(event)}
                  >
                    Presenze
                  </Button>
                )}
                <button
                  type="button"
                  className="dashboard-icon-btn"
                  title="Modifica evento"
                  aria-label={`Modifica ${event.name}`}
                  onClick={() => openEdit(event)}
                >
                  <EditIcon />
                </button>
                <button
                  type="button"
                  className="dashboard-icon-btn"
                  title="Elimina evento"
                  aria-label={`Elimina ${event.name}`}
                  onClick={() => setDeleteTarget(event)}
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="dashboard-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
        <div>
          <h1 className="dashboard-card__title">Eventi</h1>
          <p className="dashboard-card__subtitle">Crea, modifica ed elimina gli eventi del tuo gestionale.</p>
        </div>
        <Button className="users-edit-btn" onClick={openCreate}>Nuovo evento</Button>
      </div>

      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}
      {loading && <p>Caricamento...</p>}

      {!loading && (
        <>
          <EventSection title="Eventi in corso" items={inProgress} />
          <EventSection title="Eventi in programma" items={upcoming} />
          <EventSection title="Eventi passati" items={past} showReports />
        </>
      )}

      <Modal show={showForm} onHide={closeForm} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingEvent ? "Modifica evento" : "Nuovo evento"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {formError && <FlashAlert variant="danger" onHidden={() => setFormError("")}>{formError}</FlashAlert>}
            <Form.Group className="mb-3">
              <Form.Label>Copertina</Form.Label>
              <Form.Control type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(e) => setForm((prev) => ({ ...prev, coverFile: e.target.files?.[0] || null }))} />
              <Form.Text>Formati supportati: PNG, JPG, GIF e WebP.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><RequiredLabel>Titolo</RequiredLabel></Form.Label>
              <Form.Control required aria-required="true" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><RequiredLabel>Descrizione</RequiredLabel></Form.Label>
              <Form.Control as="textarea" rows={3} required aria-required="true" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><RequiredLabel>Link YouTube</RequiredLabel></Form.Label>
              <Form.Control required aria-required="true" value={form.embed_link} onChange={(e) => setForm((prev) => ({ ...prev, embed_link: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><RequiredLabel>Data e ora (fuso orario Roma)</RequiredLabel></Form.Label>
              <Form.Control type="datetime-local" required aria-required="true" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} />
            </Form.Group>
            {isSuperadmin && !editingEvent && (
              <Form.Group className="mb-3">
                <Form.Label><RequiredLabel>Applicazione</RequiredLabel></Form.Label>
                <Form.Select
                  required
                  aria-required="true"
                  value={form.application_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, application_id: e.target.value }))}
                >
                  <option value="">Seleziona applicazione</option>
                  {applications.map((app) => (
                    <option key={app.id} value={app.id}>{app.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeForm}>Annulla</Button>
            <Button type="submit" className="users-edit-btn" disabled={saving || (isSuperadmin && !editingEvent && !form.application_id)}>
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Elimina evento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Per eliminare <strong>{deleteTarget?.name}</strong> inserisci l&apos;ID dell&apos;evento.</p>
          <Form.Control
            value={deleteUid}
            onChange={(e) => setDeleteUid(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="ID"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Annulla</Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving || !deleteUid.trim()}>
            Elimina
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(attendanceTarget)}
        onHide={() => setAttendanceTarget(null)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Presenze — {attendanceTarget?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {attendanceLoading && <p>Caricamento presenze...</p>}
          {attendanceError && <Alert variant="danger">{attendanceError}</Alert>}
          {!attendanceLoading && !attendanceError && attendanceUsers.length === 0 && (
            <Alert variant="info">Nessuna presenza registrata per questo evento.</Alert>
          )}
          {!attendanceLoading && !attendanceError && attendanceUsers.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th>Partecipante</th>
                    <th>Email</th>
                    <th>Codice fiscale</th>
                    <th>Entrata</th>
                    <th>Uscita</th>
                    <th>Presenza</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceUsers.map((user) => (
                    <tr key={user.username}>
                      <td>{`${user.first_name || ""} ${user.last_name || ""}`.trim() || "—"}</td>
                      <td>{user.username}</td>
                      <td>{user.codice_fiscale || "—"}</td>
                      <td>{user.entrata || "—"}</td>
                      <td>{user.uscita || "—"}</td>
                      <td>{user.presenza || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setAttendanceTarget(null)}>
            Chiudi
          </Button>
          {attendanceTarget && (
            <>
              <Button
                variant="outline-primary"
                disabled={Boolean(reportBusy)}
                onClick={() => downloadAttendance(attendanceTarget, "xlsx")}
              >
                {reportBusy === `${attendanceTarget.id}-xlsx` ? "Download..." : "Scarica XLSX"}
              </Button>
              <Button
                className="users-edit-btn"
                disabled={Boolean(reportBusy)}
                onClick={() => downloadAttendance(attendanceTarget, "pdf")}
              >
                {reportBusy === `${attendanceTarget.id}-pdf` ? "Download..." : "Scarica PDF"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardGestoreEvents;
